import {
  apenasDigitos,
  LIMITE_TEXTO,
  LIMITE_TEXTO_LONGO,
  MENSAGEM,
  validarCnpj,
  validarCpf,
  validarEmail,
  validarTelefone,
} from '@/lib/mascaras';

// Formulários definidos pelo backend.
//
// A definição vem da função `formulario`; esta camada apenas interpreta o que
// chegou. As regras aqui existem para dar retorno imediato ao usuário — a
// decisão é sempre do servidor, que revalida tudo em
// `supabase/functions/_shared/formulario.ts`. Se as duas divergirem, vale a de
// lá; esta pode no máximo ser mais permissiva.

export interface Condicao {
  campo: string;
  operador: string;
  valor: unknown;
}

export interface CampoDefinicao {
  chave: string;
  rotulo: string;
  ajuda: string | null;
  tipo: string;
  obrigatorio: boolean;
  ordem: number;
  grupo: string | null;
  opcoes: Array<{ valor: string; rotulo: string }> | null;
  origem_opcoes: string | null;
  validacoes: Record<string, unknown>;
  condicao: Condicao | null;
}

export interface FormularioDefinicao {
  id: string;
  titulo: string;
  descricao: string;
  versao: number;
  escopo: string;
  campos: CampoDefinicao[];
}

export type Respostas = Record<string, unknown>;

export function satisfaz(operador: string, respondido: unknown, esperado: unknown): boolean {
  switch (operador) {
    case 'igual':
      return JSON.stringify(respondido) === JSON.stringify(esperado);
    case 'diferente':
      return JSON.stringify(respondido) !== JSON.stringify(esperado);
    case 'maior_que':
      return Number(respondido) > Number(esperado);
    case 'menor_que':
      return Number(respondido) < Number(esperado);
    case 'contem':
      return Array.isArray(respondido) && respondido.includes(esperado);
    case 'preenchido':
      return respondido !== null && respondido !== undefined && respondido !== '';
    default:
      return false;
  }
}

/** Campo escondido não é exigido: travar o envio por um campo invisível seria um beco sem saída. */
export function campoVisivel(campo: CampoDefinicao, respostas: Respostas): boolean {
  if (!campo.condicao) return true;
  return satisfaz(campo.condicao.operador, respostas[campo.condicao.campo], campo.condicao.valor);
}

export function camposVisiveis(
  definicao: FormularioDefinicao,
  respostas: Respostas,
): CampoDefinicao[] {
  return definicao.campos.filter((campo) => campoVisivel(campo, respostas));
}

/** Agrupa preservando a ordem de `ordem`, para a tela montar seções. */
export function agruparPorSecao(campos: CampoDefinicao[]): Array<[string, CampoDefinicao[]]> {
  const grupos = new Map<string, CampoDefinicao[]>();
  for (const campo of campos) {
    const nome = campo.grupo || 'Geral';
    if (!grupos.has(nome)) grupos.set(nome, []);
    grupos.get(nome)!.push(campo);
  }
  return [...grupos.entries()];
}

const vazio = (valor: unknown): boolean =>
  valor === null ||
  valor === undefined ||
  valor === '' ||
  (Array.isArray(valor) && valor.length === 0);

function validarTipo(campo: CampoDefinicao, valor: unknown): string | null {
  const texto = String(valor);
  switch (campo.tipo) {
    case 'numero':
      return Number.isFinite(Number(valor)) ? null : 'Informe um número.';
    case 'data':
      return Number.isNaN(Date.parse(texto)) ? 'Data inválida.' : null;
    // Documentos e telefone usam a mesma validação do resto da aplicação,
    // incluindo dígito verificador — conferir só o tamanho aceitaria
    // 111.111.111-11.
    case 'cpf':
      return validarCpf(texto) ? null : MENSAGEM.cpf;
    case 'cnpj':
      return validarCnpj(texto) ? null : MENSAGEM.cnpj;
    case 'cep':
      return apenasDigitos(texto).length === 8 ? null : MENSAGEM.cep;
    case 'telefone':
      return validarTelefone(texto) ? null : MENSAGEM.telefone;
    case 'email':
      return validarEmail(texto) ? null : MENSAGEM.email;
    default:
      return null;
  }
}

function validarRegras(campo: CampoDefinicao, valor: unknown): string | null {
  const v = campo.validacoes || {};

  // Campo de texto sem limite declarado usa o padrão do produto.
  const limitePadrao = campo.tipo === 'textarea' ? LIMITE_TEXTO_LONGO : LIMITE_TEXTO;
  const maximo = typeof v.maxLength === 'number' ? v.maxLength : limitePadrao;
  if (String(valor).length > maximo) return `Máximo de ${maximo} caracteres.`;

  if (typeof v.min === 'number' && Number(valor) < v.min) return `Mínimo: ${v.min}.`;
  if (typeof v.max === 'number' && Number(valor) > v.max) return `Máximo: ${v.max}.`;
  if (typeof v.minLength === 'number' && String(valor).length < v.minLength) {
    return `Mínimo de ${v.minLength} caracteres.`;
  }
  if (typeof v.padrao === 'string' && !new RegExp(v.padrao).test(String(valor))) {
    return 'Formato inválido.';
  }
  return null;
}

export function validar(
  definicao: FormularioDefinicao,
  respostas: Respostas,
): Record<string, string> {
  const erros: Record<string, string> = {};

  for (const campo of camposVisiveis(definicao, respostas)) {
    const valor = respostas[campo.chave];

    if (vazio(valor)) {
      if (campo.obrigatorio) erros[campo.chave] = 'Campo obrigatório.';
      continue;
    }

    const erro = validarTipo(campo, valor) || validarRegras(campo, valor);
    if (erro) erros[campo.chave] = erro;
  }

  return erros;
}

/** Descarta respostas de campos que deixaram de estar visíveis. */
export function limparOcultos(definicao: FormularioDefinicao, respostas: Respostas): Respostas {
  const visiveis = new Set(camposVisiveis(definicao, respostas).map((c) => c.chave));
  const limpo: Respostas = {};
  for (const [chave, valor] of Object.entries(respostas)) {
    if (visiveis.has(chave)) limpo[chave] = valor;
  }
  return limpo;
}
